import React from 'react';
import { CheckCircle2, ScanLine, Smartphone, Truck, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type ResolvedOrder = {
  id: string;
  reference: string;
  status: string;
  deliverySpeed?: string;
  recipientName?: string;
  recipientPhone?: string | null;
  addressLine?: string;
  canComplete: boolean;
};

type AssignmentOrder = {
  id: string;
  reference: string;
  status: string;
  recipientName: string;
  recipientPhone: string | null;
  addressLine: string;
  delivered: boolean;
};

type AssignmentBatch = {
  id: string;
  name: string | null;
  areaLabel: string;
  status: string;
  windowStart: string;
  windowEnd: string;
  deliverySpeed: 'STANDARD' | 'EXPRESS';
  orderCount: number;
  orders: AssignmentOrder[];
};

type DetectorCtor = new (opts?: { formats?: string[] }) => {
  detect: (source: HTMLVideoElement | HTMLCanvasElement | ImageBitmapSource) => Promise<
    Array<{ rawValue?: string }>
  >;
};

/** Status set the agent can act on (matches backend). */
const COMPLETABLE_STATUSES = new Set(['SHIPPED', 'RESHIPPED']);
const DELIVERED_STATUSES = new Set(['DELIVERED', 'CLOSED']);

export default function DeliveryScanPage() {
  const { authorizedFetch } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [code, setCode] = React.useState(() => String(searchParams.get('code') || '').trim());
  const [loading, setLoading] = React.useState(false);
  const [cameraOn, setCameraOn] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [resolved, setResolved] = React.useState<ResolvedOrder | null>(null);
  const [completing, setCompleting] = React.useState(false);
  const [assignments, setAssignments] = React.useState<AssignmentBatch[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = React.useState(true);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const mediaRef = React.useRef<MediaStream | null>(null);
  const scanTimerRef = React.useRef<number | null>(null);

  const stopCamera = React.useCallback(() => {
    if (scanTimerRef.current) {
      window.clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }
    if (mediaRef.current) {
      mediaRef.current.getTracks().forEach((t) => t.stop());
      mediaRef.current = null;
    }
    setCameraOn(false);
  }, []);

  React.useEffect(() => () => stopCamera(), [stopCamera]);

  const loadAssignments = React.useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/sales/orders/scan-delivery/my-assignments`,
        { headers: { 'x-department-slug': '' } },
      );
      if (!res.ok) {
        setAssignments([]);
        return;
      }
      const list = (await res.json()) as AssignmentBatch[];
      setAssignments(Array.isArray(list) ? list : []);
    } catch {
      setAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [authorizedFetch]);

  React.useEffect(() => {
    void loadAssignments();
  }, [loadAssignments]);

  const resolveCode = React.useCallback(async () => {
    const normalized = code.trim();
    if (!normalized) {
      setError('Scan or enter a barcode/QR code first.');
      setResolved(null);
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/sales/orders/scan-delivery/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-department-slug': '',
        },
        body: JSON.stringify({ code: normalized }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        order?: ResolvedOrder;
      };
      if (!res.ok || !body.order) {
        throw new Error(body.message || 'Unable to validate this code.');
      }
      setResolved(body.order);
      // If a previously-resolved order is already delivered, give the agent
      // an immediate confirmation banner instead of leaving them to read the
      // status badge.
      if (DELIVERED_STATUSES.has(body.order.status)) {
        setSuccess(`${body.order.reference} is already delivered.`);
      }
    } catch (e) {
      setResolved(null);
      setError(e instanceof Error ? e.message : 'Unable to validate this code.');
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, code]);

  const completeDelivery = React.useCallback(async () => {
    const normalized = code.trim();
    if (!normalized) {
      setError('Scan or enter a barcode/QR code first.');
      return;
    }
    setCompleting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/sales/orders/scan-delivery/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-department-slug': '',
        },
        body: JSON.stringify({ code: normalized }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        alreadyDelivered?: boolean;
        order?: { reference?: string; status?: string };
      };
      if (!res.ok) {
        throw new Error(body.message || 'Unable to complete delivery.');
      }
      setSuccess(
        body.alreadyDelivered
          ? `${body.order?.reference || 'Order'} is already delivered (${body.order?.status || 'DELIVERED'}).`
          : `${body.order?.reference || 'Order'} marked as delivered successfully.`,
      );
      await Promise.all([resolveCode(), loadAssignments()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to complete delivery.');
    } finally {
      setCompleting(false);
    }
  }, [authorizedFetch, code, resolveCode, loadAssignments]);

  const startCamera = React.useCallback(async () => {
    const BarcodeDetectorCtor = (window as unknown as { BarcodeDetector?: DetectorCtor })
      .BarcodeDetector;
    if (!BarcodeDetectorCtor) {
      setError('Camera barcode scanning is not supported on this browser. Use manual input.');
      return;
    }
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      mediaRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
      const detector = new BarcodeDetectorCtor({
        formats: ['code_128', 'qr_code'],
      });
      scanTimerRef.current = window.setInterval(async () => {
        const video = videoRef.current;
        if (!video || video.readyState < 2) return;
        try {
          const rows = await detector.detect(video);
          const hit = rows.find((r) => String(r.rawValue || '').trim());
          if (!hit?.rawValue) return;
          // QR codes encode a full URL — if it contains ?code=..., extract it,
          // otherwise treat the raw value (Code-128 order reference) as the code.
          const raw = String(hit.rawValue).trim();
          let extracted = raw;
          try {
            const u = new URL(raw);
            const fromQuery = u.searchParams.get('code');
            if (fromQuery) extracted = fromQuery;
          } catch {
            // Not a URL — leave as-is.
          }
          setCode(extracted);
          stopCamera();
        } catch {
          // Keep scanning silently; user can fallback to manual.
        }
      }, 900);
    } catch {
      setError('Unable to access camera. Check phone/browser permissions.');
      stopCamera();
    }
  }, [stopCamera]);

  React.useEffect(() => {
    if (!code) return;
    void resolveCode();
  }, [code, resolveCode]);

  /** Tapping a row in "My orders to deliver" prefills + auto-resolves it. */
  const pickOrder = React.useCallback(
    (order: AssignmentOrder) => {
      setCode(order.reference);
      setError(null);
      setSuccess(null);
      // Reflect the chosen code in the URL so a refresh keeps the same context.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('code', order.reference);
        return next;
      });
    },
    [setSearchParams],
  );

  const isDelivered = resolved && DELIVERED_STATUSES.has(resolved.status);
  const canMarkDelivered = Boolean(
    resolved && resolved.canComplete && COMPLETABLE_STATUSES.has(resolved.status),
  );

  const totalAssignedOrders = assignments.reduce((sum, b) => sum + b.orders.length, 0);
  const pendingOrders = assignments.reduce(
    (sum, b) => sum + b.orders.filter((o) => !o.delivered).length,
    0,
  );

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-5 text-slate-100">
      <div className="mx-auto max-w-xl rounded-2xl border border-slate-700 bg-slate-900/90 p-4 shadow-2xl">
        <div className="mb-3 flex items-center gap-2">
          <Truck className="h-5 w-5 text-emerald-400" />
          <h1 className="text-lg font-bold tracking-tight">Delivery Scan</h1>
        </div>
        <p className="text-sm text-slate-300">
          Scan a parcel QR/barcode or pick an order below to mark it delivered.
        </p>

        <div className="mt-4 space-y-2">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
            Barcode / QR value
          </label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="# ORD-xxxx..."
            className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-400"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void resolveCode()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-semibold hover:bg-slate-700 disabled:opacity-50"
            >
              <ScanLine className="h-3.5 w-3.5" /> Validate code
            </button>
            {!cameraOn ? (
              <button
                type="button"
                onClick={() => void startCamera()}
                className="inline-flex items-center gap-1.5 rounded-full border border-indigo-400/70 bg-indigo-500/20 px-3 py-1.5 text-xs font-semibold text-indigo-100 hover:bg-indigo-500/30"
              >
                <Smartphone className="h-3.5 w-3.5" /> Use camera
              </button>
            ) : (
              <button
                type="button"
                onClick={stopCamera}
                className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/70 bg-rose-500/20 px-3 py-1.5 text-xs font-semibold text-rose-100 hover:bg-rose-500/30"
              >
                Stop camera
              </button>
            )}
          </div>
        </div>

        {cameraOn ? (
          <div className="mt-3 overflow-hidden rounded-xl border border-slate-700 bg-black">
            <video ref={videoRef} className="h-64 w-full object-cover" playsInline muted />
          </div>
        ) : null}

        {error ? (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-400/50 bg-rose-500/10 p-3 text-sm text-rose-100">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{error}</p>
          </div>
        ) : null}
        {success ? (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-400/50 bg-emerald-500/10 p-3 text-sm text-emerald-100">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{success}</p>
          </div>
        ) : null}

        {resolved ? (
          <div className="mt-4 rounded-xl border border-slate-700 bg-slate-950/70 p-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-slate-100">{resolved.reference}</p>
              <span
                className={[
                  'rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                  isDelivered
                    ? 'bg-emerald-500/20 text-emerald-200'
                    : 'bg-slate-800 text-slate-200',
                ].join(' ')}
              >
                {resolved.status}
              </span>
            </div>
            <p className="mt-1 text-slate-300">Recipient: {resolved.recipientName || '—'}</p>
            {resolved.recipientPhone ? (
              <p className="text-slate-300">Phone: {resolved.recipientPhone}</p>
            ) : null}
            {resolved.addressLine ? <p className="text-slate-300">Address: {resolved.addressLine}</p> : null}
            <div className="mt-3">
              {isDelivered ? (
                <button
                  type="button"
                  disabled
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-100"
                >
                  <CheckCircle2 className="h-4 w-4" /> Already delivered
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void completeDelivery()}
                  disabled={completing || !canMarkDelivered}
                  className="w-full rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {completing ? 'Processing...' : 'Validate and set DELIVERED'}
                </button>
              )}
            </div>
            {!isDelivered && !canMarkDelivered ? (
              <p className="mt-2 text-xs text-amber-300">
                This order is not in a shippable completion state.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 border-t border-slate-800 pt-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold tracking-wide text-slate-200">
              My orders to deliver
            </h2>
            <button
              type="button"
              onClick={() => void loadAssignments()}
              className="text-[11px] font-semibold text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
            >
              Refresh
            </button>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            {assignmentsLoading
              ? 'Loading...'
              : totalAssignedOrders === 0
                ? 'No batch is currently assigned to you.'
                : `${pendingOrders} pending · ${totalAssignedOrders - pendingOrders} delivered`}
          </p>

          {assignments.length > 0 ? (
            <ul className="mt-3 space-y-3">
              {assignments.map((batch) => (
                <li
                  key={batch.id}
                  className="rounded-xl border border-slate-800 bg-slate-950/40 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-100">
                        {batch.name || batch.areaLabel}
                      </p>
                      {batch.name ? (
                        <p className="truncate text-[11px] text-slate-400">{batch.areaLabel}</p>
                      ) : null}
                    </div>
                    <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-300">
                      {batch.deliverySpeed}
                    </span>
                  </div>
                  <ul className="mt-2 divide-y divide-slate-800">
                    {batch.orders.length === 0 ? (
                      <li className="py-2 text-[11px] text-slate-400">
                        No deliverable orders in this batch yet.
                      </li>
                    ) : (
                      batch.orders.map((o) => (
                        <li key={o.id}>
                          <button
                            type="button"
                            onClick={() => pickOrder(o)}
                            className="flex w-full items-start justify-between gap-2 py-2 text-left transition hover:bg-slate-900/40"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-xs font-mono font-semibold text-slate-100">
                                {o.reference}
                              </p>
                              <p className="truncate text-[11px] text-slate-300">
                                {o.recipientName}
                              </p>
                              {o.addressLine ? (
                                <p className="truncate text-[11px] text-slate-400">
                                  {o.addressLine}
                                </p>
                              ) : null}
                            </div>
                            <span
                              className={[
                                'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
                                o.delivered
                                  ? 'bg-emerald-500/20 text-emerald-200'
                                  : o.status === 'SHIPPED' || o.status === 'RESHIPPED'
                                    ? 'bg-amber-500/20 text-amber-200'
                                    : 'bg-slate-800 text-slate-300',
                              ].join(' ')}
                            >
                              {o.delivered ? 'Delivered' : o.status}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </main>
  );
}
